"use client";
import React, { useState, useId } from "react";
import { Form, Input, Button, App } from "antd";
import { useTranslations } from "next-intl";
import { sendForm } from "@/lib/api/form";
import { get_inquiry_blocking_keyword } from "@/lib/api/common";

const { TextArea } = Input;

export async function getBlockKeys() {
  try {
    return await get_inquiry_blocking_keyword();
  } catch (e) {
    // console.log(e);
    return { content_blocking_keyword_list: [], email_blocking_keyword_list: [] };
  }
}

const FooterContactForm = () => {
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [form] = Form.useForm();
  const t = useTranslations();
  const { message } = App.useApp();
  const formId = useId(); // 为整个表单生成唯一ID前缀

  const onFinish = async (values: any) => {
    // 获取屏蔽关键词
    const blockObj = await getBlockKeys();
    const { content_blocking_keyword_list, email_blocking_keyword_list } = blockObj;
    
    // 检查是否包含屏蔽关键词的通用函数
    const containsBlockedKeyword = (input: string | undefined, keywords: string[]): boolean => {
      if (!input || !keywords.length) return false;
      return keywords.some((key) => input.includes(key));
    };

    // 验证内容和邮箱
    const isContentInvalid = 
      containsBlockedKeyword(values.message, content_blocking_keyword_list) ||
      containsBlockedKeyword(values.email, email_blocking_keyword_list);
    
    if (isContentInvalid) {
      message.error(t("form.contentisnotstandardized"));
      return;
    }

    try {
      const sendObj = { source: 'footer-contact', type: 0, chart: "" };
      const inquiry_info: any = {
        form: [],
        contact_method: values.email && values.tel ? 3 : 1,
        html: `<h1>您的网站 <a href="${process.env.NEXT_PUBLIC_SITE_URL}" target="_blank">${process.env.NEXT_PUBLIC_SITE_URL}</a> 有新询盘，请前往网站后台查看详细内容！</h1>
<h2>询盘信息</h2>
<p style="padding: 5px 10px;border:1px solid #ccc;border-radius: 10px;margin: 10px;">名字：${values.name}</p>
<p style="padding: 5px 10px;border:1px solid #ccc;border-radius: 10px;margin: 10px;">邮箱：<a href='mailto:${values.email}' target="_blank"> ${values.email}</a></p>
<p style="padding: 5px 10px;border:1px solid #ccc;border-radius: 10px;margin: 10px;">电话：${values.tel}</p>
<p style="padding: 5px 10px;border:1px solid #ccc;border-radius: 10px;margin: 10px;">留言：${values.message}</p>
`,
      };

      // 构建表单数据
      Object.keys(values).forEach((key) => {
        if (key === "message") {
          inquiry_info.form.push({ label: key, type: "textarea", value: values[key] });
          inquiry_info[key] = values[key];
        } else {
          inquiry_info.form.push({ label: key, type: "input", value: values[key] });
          inquiry_info[key] = values[key];
        }
      });

      sendObj.chart = JSON.stringify({ inquiry_info });
      setLoadingBtn(true);
      const response = (await sendForm(sendObj)) as any;
      
      if (response && response.code === 200) {
        void message.success(t("form.sendSuccess"));
        form.resetFields(); // 重置表单
      } else {
        void message.error(response?.message || t("form.sendFail"));
      }
    } catch (error) {
      message.error(t("form.sendFail"));
    } finally {
      setLoadingBtn(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error("Failed:", errorInfo);
    message.error(t("Error.inqFillTip"));
  };

  return (
    <div className="bg-transparent px-5 rounded-lg max-md:px-0">
      {/* 添加全局样式标签，使用标准CSS语法 */}
      <style>
        {`
          .footer-form-input::placeholder,
          .footer-form-textarea::placeholder {
            color: white !important;
            opacity: 0.7;
          }
          .footer-form-input::-webkit-input-placeholder,
          .footer-form-textarea::-webkit-input-placeholder {
            color: white !important;
            opacity: 0.7;
          }
          .footer-form-input::-moz-placeholder,
          .footer-form-textarea::-moz-placeholder {
            color: white !important;
            opacity: 0.7;
          }
          .footer-form-input:-ms-input-placeholder,
          .footer-form-textarea:-ms-input-placeholder {
            color: white !important;
            opacity: 0.7;
          }
          .required-asterisk {
            color: red;
            margin-right: 4px;
          }
        `}
      </style>
      
      <h3 className="text-2xl text-white mb-6 text-center">{t("footer.Contact-us-now")}</h3>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{ remember: true }}
        id={`footer-contact-form-${formId}`}
      >
        <Form.Item
          name="email"
          rules={[
            { required: true, message: t("form.pleaseEmail") },
            { type: "email", message:  t("form.vailEmailTip") }
          ]}
          className="mb-4"
        >
          <Input 
            id={`email-${formId}`}
            autoComplete="email"
            placeholder={t('footer.email')} 
            className="footer-form-input !bg-white/30 !text-white !rounded-none !h-12" // 添加自定义类
            style={{ 
              backgroundColor: '#1e40af', 
              borderColor: '#1e40af', 
              color: 'white',
              borderRadius: 0,
              height: '48px'
            }}
          />
        </Form.Item>
        
        <Form.Item
          name="name"
          rules={[{ required: false, message: t("form.pleaseName") }]}
          className="mb-4"
        >
          <Input 
            id={`name-${formId}`}
            autoComplete="name"
            placeholder={t("form.name")} 
            className="footer-form-input !bg-white/30 !text-white !rounded-none !h-12" // 添加自定义类
            style={{ 
              backgroundColor: '#1e40af', 
              borderColor: '#1e40af', 
              color: 'white',
              borderRadius: 0,
              height: '48px'
            }}
          />
        </Form.Item>
        
        <Form.Item
          name="tel"
          rules={[{ required: false, message: t("form.pleasePhone") }]}
          className="mb-4"
        >
          <Input 
            placeholder={t("footer.Tel")}
            className="footer-form-input !bg-white/30 !text-white !rounded-none !h-12" // 添加自定义类
            style={{ 
              backgroundColor: '#1e40af', 
              borderColor: '#1e40af', 
              color: 'white',
              borderRadius: 0,
              height: '48px'
            }}
          />
        </Form.Item>
        
        <Form.Item
          name="message"
          rules={[{ required: false, message: t("form.pleaseMessage") }]}
          className="mb-6"
        >
          <TextArea 
            id={`message-${formId}`}
            rows={4} 
            placeholder={t("form.message")}
            className="footer-form-textarea !bg-white/30 !text-white !rounded-none" // 添加自定义类
            style={{ 
              backgroundColor: '#1e40af', 
              borderColor: '#1e40af', 
              color: 'white',
              borderRadius: 0
            }}
          />
        </Form.Item>
        
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            className="!w-full !bg-white !text-mainColor !rounded-none !py-5 !text-lg"
            style={{ 
              backgroundColor: 'white', 
              color: '#1e3a8a', 
              borderRadius: 0,
              padding: '12px 0',
              fontWeight: 'bold'
            }}
            loading={loadingBtn}
          >
            {t("base.Submit")}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default FooterContactForm;